'use client'

import { ChevronDown } from 'lucide-react'
import { NavigationItem } from './FourThreeOneNav'

// BIRHAUS: Utility function (replaces @/lib/utils cn)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// BIRHAUS: Self-contained Badge component
function DesktopBadge({
  children,
  variant = 'default',
  className = ''
}: {
  children: React.ReactNode
  variant?: 'default' | 'secondary' | 'destructive'
  className?: string
}) {
  const baseClasses = "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
  
  const getVariantClass = (v: typeof variant): string => {
    switch (v) {
      case 'default': return "bg-blue-100 text-blue-800"
      case 'secondary': return "bg-gray-100 text-gray-800"
      case 'destructive': return "bg-red-100 text-red-800"
      default: return "bg-blue-100 text-blue-800"
    }
  }

  return (
    <span className={cn(baseClasses, getVariantClass(variant), className)}>
      {children}
    </span>
  )
}

// Helper component for submenu rendering
function Submenu({ 
  items, 
  onClose 
}: {
  items: NavigationItem[]
  onClose: () => void
}) {
  return (
    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50">
      <div className="py-1">
        {items.map((subitem) => (
          <button
            key={subitem.id}
            onClick={() => {
              subitem.onClick?.()
              onClose()
            }}
            disabled={subitem.disabled}
            className={cn(
              "w-full text-left px-4 py-2 text-sm transition-colors",
              "hover:bg-gray-50 focus:outline-none focus:bg-gray-50",
              subitem.active && "text-blue-700 bg-blue-50",
              subitem.disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <div className="flex items-center space-x-2">
              {subitem.icon && <span className="flex-shrink-0">{subitem.icon}</span>}
              <span>{subitem.label}</span>
              {subitem.badge && (
                <DesktopBadge variant="secondary" className="ml-auto">
                  {subitem.badge}
                </DesktopBadge>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// Helper component for navigation item rendering
function NavItem({ 
  item, 
  activeSubmenu, 
  onItemClick, 
  onSubmenuToggle 
}: {
  item: NavigationItem
  activeSubmenu: string | null
  onItemClick: (item: NavigationItem) => void
  onSubmenuToggle: (itemId: string | null) => void
}) {
  return (
    <div className="relative">
      <button
        onClick={() => onItemClick(item)}
        disabled={item.disabled}
        className={cn(
          "flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
          item.active 
            ? "text-blue-700 bg-blue-50"
            : "text-gray-700 hover:text-gray-900 hover:bg-gray-50",
          item.disabled && "opacity-50 cursor-not-allowed"
        )}
        aria-current={item.active ? 'page' : undefined}
        aria-expanded={item.submenu ? activeSubmenu === item.id : undefined}
      >
        {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
        <span>{item.label}</span>
        {item.badge && (
          <DesktopBadge variant="default" className="ml-1">
            {item.badge}
          </DesktopBadge>
        )}
        {item.submenu && (
          <ChevronDown className="ml-1 h-4 w-4 flex-shrink-0" />
        )}
      </button>
      
      {/* Submenu */}
      {item.submenu && activeSubmenu === item.id && (
        <Submenu 
          items={item.submenu} 
          onClose={() => onSubmenuToggle(null)}
        />
      )}
    </div>
  )
}

// Overflow menu component
function OverflowMenu({
  items,
  activeSubmenu,
  onItemClick,
  onSubmenuToggle,
  overflowLabel
}: {
  items: NavigationItem[]
  activeSubmenu: string | null
  onItemClick: (item: NavigationItem) => void
  onSubmenuToggle: (itemId: string | null) => void
  overflowLabel: string
}) {
  if (items.length === 0) return null

  return (
    <div className="relative">
      <button
        onClick={() => onSubmenuToggle(activeSubmenu === 'overflow' ? null : 'overflow')}
        className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <span>{overflowLabel}</span>
        <ChevronDown className="ml-1 h-4 w-4" />
      </button>
      
      {activeSubmenu === 'overflow' && (
        <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          <div className="py-1">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  onItemClick(item)
                  onSubmenuToggle(null)
                }}
                disabled={item.disabled}
                className={cn(
                  "w-full text-left px-4 py-2 text-sm transition-colors",
                  "hover:bg-gray-50 focus:outline-none focus:bg-gray-50",
                  item.active && "text-blue-700 bg-blue-50",
                  item.disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="flex items-center space-x-2">
                  {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                  <span>{item.label}</span>
                  {item.badge && (
                    <DesktopBadge variant="secondary" className="ml-auto">
                      {item.badge}
                    </DesktopBadge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface DesktopNavigationProps {
  visibleNavItems: NavigationItem[]
  overflowNavItems: NavigationItem[]
  activeSubmenu: string | null
  onItemClick: (item: NavigationItem) => void
  onSubmenuToggle: (itemId: string | null) => void
  overflowBehavior: 'hide' | 'collapse' | 'scroll'
  overflowLabel: string
  bp: string
}

export function DesktopNavigation({
  visibleNavItems,
  overflowNavItems,
  activeSubmenu,
  onItemClick,
  onSubmenuToggle,
  overflowBehavior,
  overflowLabel,
  bp
}: DesktopNavigationProps) {
  const hasOverflow = overflowNavItems.length > 0

  return (
    <div className={cn('hidden', `${bp}:flex`, 'items-center space-x-8')}>
      {visibleNavItems.map((item) => (
        <NavItem
          key={item.id}
          item={item}
          activeSubmenu={activeSubmenu}
          onItemClick={onItemClick}
          onSubmenuToggle={onSubmenuToggle}
        />
      ))}
      
      {/* BIRHAUS: Overflow menu for items > 4 */}
      {hasOverflow && overflowBehavior === 'collapse' && (
        <OverflowMenu
          items={overflowNavItems}
          activeSubmenu={activeSubmenu}
          onItemClick={onItemClick}
          onSubmenuToggle={onSubmenuToggle}
          overflowLabel={overflowLabel}
        />
      )}
    </div>
  )
}
